import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { BaseFilter } from 'src/components/Common/Filter/BaseFilter'
import { AutoCompleteOption } from 'src/components/Common/FjFilter'
import { FjPopoverContent } from 'src/components/Common/FjPopover'
import { Course } from 'src/models/Course'
import { Hub } from 'src/models/Hub'
import { User } from 'src/models/User'
import { debounce } from 'src/utils/debounce'
import { DEFAULT_SELECT_PAGE_SIZE } from 'src/constants/app'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import { TemplateableLearningContentType } from 'src/components/Feed/TemplateAuthoringModal'
import { getContentCategoryTitle } from 'src/utils/content'

export type AutoCompleteFilterProps = {
  paramKey: string
  label: string
  initialOptions: AutoCompleteOption[]
  multiple?: boolean
  clearable?: boolean
  onChange: (params: object, option?: AutoCompleteOption) => void
  onSearch: (value: string) => Promise<AutoCompleteOption[]>
}

export const AutoCompleteFilter = ({
  paramKey,
  label,
  initialOptions,
  multiple = true,
  clearable = true,
  onChange,
  onSearch,
}: AutoCompleteFilterProps) => {
  const [options, setOptions] = useState<AutoCompleteOption[]>([])
  const [selectedOptions, setSelectedOptions] = useState<AutoCompleteOption[]>([])

  useEffect(() => {
    setSelectedOptions(initialOptions)
  }, [initialOptions])

  const handleSearch = debounce(async (value) => {
    const options = await onSearch(value)
    setOptions(options)
  }, 300)

  const handleAddFilterOption = (option: AutoCompleteOption) => {
    const newOptions = multiple ? [...selectedOptions, option] : [option]
    setSelectedOptions(newOptions)
    onChange({ [paramKey]: newOptions.map((option) => option.id).join(',') || undefined }, option)
  }

  const handleRemoveFilterOption = (option: AutoCompleteOption) => {
    const newOptions = selectedOptions.filter((selectedOption) => selectedOption.id !== option.id)
    setSelectedOptions(newOptions)
    onChange({ [paramKey]: newOptions.map((option) => option.id).join(',') || undefined })
  }

  const handleClear = () => {
    setSelectedOptions([])
    onChange({
      [paramKey]: undefined,
    })
  }

  const filterLabel = useMemo(() => {
    let newFilterLabel = label
    if (selectedOptions.length > 0) {
      newFilterLabel =
        selectedOptions.length > 2
          ? `${newFilterLabel} | ${selectedOptions[0].label}, ${selectedOptions[1].label}, + ${
              selectedOptions.length - 2
            } more`
          : `${newFilterLabel} | ${selectedOptions.map((filter) => filter.label).join(', ')}`
    }
    return newFilterLabel
  }, [label, selectedOptions])

  return (
    <BaseFilter
      title={filterLabel}
      content={
        <FjPopoverContent
          options={options}
          values={selectedOptions}
          onSearch={handleSearch}
          addFilterOption={handleAddFilterOption}
          removeFilterOption={clearable ? handleRemoveFilterOption : undefined}
        />
      }
      selected={selectedOptions.length > 0}
      onClear={clearable ? handleClear : undefined}
      onClick={() => handleSearch('')}
    />
  )
}

type HubFilterProps = {
  onChange: (params: object) => void
}

export const HUB_PARAM_KEY = 'hub_id'

export const HubFilter = ({ onChange }: HubFilterProps) => {
  const [initialOptions, setInitialOptions] = useState([])
  const { search } = useLocation()
  const abortController = useRef<AbortController>()

  const initFiltersFromURL = useCallback(async () => {
    const searchParams = new URLSearchParams(search)
    const param = searchParams.get(HUB_PARAM_KEY)
    if (!param) {
      setInitialOptions([])
      return
    }

    try {
      abortController.current?.abort?.()
      abortController.current = new AbortController()
      const initialOptions = (
        await Promise.all(
          param
            .split(',')
            .map((id) => Hub.get(id, undefined, undefined, { fields: 'id,name' }, abortController.current))
        )
      ).map((hub: Hub) => {
        return { ...hub, label: hub.name }
      })
      setInitialOptions(initialOptions)
    } catch (err) {
      if (err?.code !== 'ERR_CANCELED') sharedAppStateStore.handleError(err, { alert: false })
    }
  }, [search])

  useEffect(() => {
    initFiltersFromURL()
  }, [initFiltersFromURL])

  const handleSearch = async (value: string) => {
    try {
      abortController.current?.abort?.()
      abortController.current = new AbortController()
      const { data } = await Hub.list({ search: value, fields: 'id,name', page_size: DEFAULT_SELECT_PAGE_SIZE })
      const options = data.map((item: Hub) => ({ ...item, value: item.name, label: item.name }))
      return options
    } catch (err) {
      if (err?.code !== 'ERR_CANCELED') {
        sharedAppStateStore.handleError(err, { alert: false })
        return []
      }
    }
  }

  return (
    <AutoCompleteFilter
      label="Hub"
      paramKey={HUB_PARAM_KEY}
      initialOptions={initialOptions}
      onChange={onChange}
      onSearch={handleSearch}
    />
  )
}

type UserFilterProps = {
  queryParams?: object
  onChange: (params: object) => void
}

export const USER_PARAM_KEY = 'userId'

export const UserFilter: React.FC<UserFilterProps> = ({ queryParams, onChange }) => {
  const [initialOptions, setInitialOptions] = useState([])
  const { search } = useLocation()
  const abortController = useRef<AbortController>()

  const convertUserToOptions = (users: User[]): AutoCompleteOption[] =>
    users.map((user: User) => ({ key: user.id, id: user.id, value: user.id, label: user.fullName }))

  const initFiltersFromURL = useCallback(async () => {
    const searchParams = new URLSearchParams(search)
    const param = searchParams.get(USER_PARAM_KEY)
    if (!param) {
      setInitialOptions([])
      return
    }

    try {
      abortController.current?.abort?.()
      abortController.current = new AbortController()
      const initialOptions = await Promise.all(
        param
          .split(',')
          .map((id) => User.get(id, undefined, undefined, { fields: 'id,full_name' }, abortController.current))
      )
      setInitialOptions(convertUserToOptions(initialOptions.map((data) => User.fromData(data))))
    } catch (err) {
      sharedAppStateStore.handleError(err, { alert: false })
    }
  }, [search])

  const handleSearch = useCallback(
    async (value: string) => {
      try {
        abortController.current?.abort?.()
        abortController.current = new AbortController()
        const { results } = await User.list(
          { search: value, fields: 'id,full_name', page_size: DEFAULT_SELECT_PAGE_SIZE, ...queryParams },
          undefined,
          undefined,
          undefined,
          abortController.current
        )
        return convertUserToOptions(User.fromData(results))
      } catch (err) {
        sharedAppStateStore.handleError(err, { alert: false })
        return []
      }
    },
    [queryParams]
  )

  useEffect(() => {
    initFiltersFromURL()
  }, [initFiltersFromURL])

  return (
    <AutoCompleteFilter
      label="User"
      paramKey={USER_PARAM_KEY}
      initialOptions={initialOptions}
      onChange={onChange}
      onSearch={handleSearch}
    />
  )
}

type CourseFilterProps = {
  multiple?: boolean
  clearable?: boolean
  apiQueryParams?: object
  onChange: (params: object, option?: AutoCompleteOption) => void
}

export const COURSE_PARAM_KEY = 'courseId'

export const CourseFilter = ({ apiQueryParams, ...props }: CourseFilterProps) => {
  const [initialOptions, setInitialOptions] = useState([])
  const { search } = useLocation()
  const abortController = useRef<AbortController>()

  const initFiltersFromURL = useCallback(async () => {
    const searchParams = new URLSearchParams(search)
    const param = searchParams.get(COURSE_PARAM_KEY)
    if (!param) {
      setInitialOptions([])
      return
    }

    try {
      abortController.current?.abort?.()
      abortController.current = new AbortController()
      const initialOptions = (
        await Promise.all(
          param
            .split(',')
            .map((id) => Course.get(id, undefined, undefined, { fields: 'id,name' }, abortController.current))
        )
      ).map((course: Course) => {
        return { ...course, label: course.name }
      })
      setInitialOptions(initialOptions)
    } catch (err) {
      sharedAppStateStore.handleError(err, { alert: false })
    }
  }, [search])

  useEffect(() => {
    initFiltersFromURL()
  }, [initFiltersFromURL])

  const handleSearch = async (value: string) => {
    try {
      abortController.current?.abort?.()
      abortController.current = new AbortController()
      const { data } = await Course.list({
        search: value,
        fields: 'id,name',
        page_size: DEFAULT_SELECT_PAGE_SIZE,
        ...apiQueryParams,
      })
      const options = data.map((item: Course) => ({ ...item, value: item.name, label: item.name }))
      return options
    } catch (err) {
      sharedAppStateStore.handleError(err, { alert: false })
      return []
    }
  }

  return (
    <AutoCompleteFilter
      label="Course"
      paramKey={COURSE_PARAM_KEY}
      onSearch={handleSearch}
      initialOptions={initialOptions}
      {...props}
    />
  )
}

interface TemplateContentTypeFilterProps {
  apiQueryParams?: object
  onChange: (params: object, option?: AutoCompleteOption) => void
}

export const TEMPLATE_CONTENT_TYPE_KEY = 'contentType'

export const TemplateContentTypeFilter: React.FC<TemplateContentTypeFilterProps> = ({ onChange }) => {
  const [initialOptions, setInitialOptions] = useState<AutoCompleteOption[]>([])
  const { search } = useLocation()

  const allOptions: AutoCompleteOption[] = useMemo(() => {
    const templateableContentTypes: TemplateableLearningContentType[] = ['asset', 'dealroom', 'page', 'submodule']
    return templateableContentTypes.map((contentType) => ({
      id: contentType,
      value: contentType,
      label: getContentCategoryTitle(contentType).slice(0, -1),
      key: contentType,
    }))
  }, [])

  // Initialize filters from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(search)
    const param = searchParams.get(TEMPLATE_CONTENT_TYPE_KEY)
    if (!param) {
      setInitialOptions([])
      return
    }

    const selectedContentTypes = param.split(',')
    const initialOptions = allOptions.filter((option) => selectedContentTypes.includes(option.id))
    setInitialOptions(initialOptions)
  }, [search, allOptions])

  // Handle search locally - filter from all options
  const handleSearch = useCallback(
    async (value: string): Promise<AutoCompleteOption[]> => {
      if (!value.trim()) return allOptions

      const searchTerm = value.toLowerCase()
      return allOptions.filter(
        (option) => option.label.toLowerCase().includes(searchTerm) || option.id.toLowerCase().includes(searchTerm)
      )
    },
    [allOptions]
  )

  return (
    <AutoCompleteFilter
      label="Content Type"
      paramKey={TEMPLATE_CONTENT_TYPE_KEY}
      initialOptions={initialOptions}
      onChange={onChange}
      onSearch={handleSearch}
      multiple={false}
      clearable
    />
  )
}
